import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    RequestError,
    CustomQuestionMode,
    DEFAULT_MIN_HEIGHT,
    MAX_FRAME_HEIGHT,
    READY_TIMEOUT_MS,
    resolveSettings,
    type CustomQuestionClientHistory,
    type CustomQuestionConfirmation,
    type CustomQuestionDefinition,
    type CustomQuestionModeName,
} from './CustomQuestionApi';
import { buildSandboxDocument } from './sandboxDocument';
import type { FormStyles } from '~/lib/FormTheme';

type UseCustomQuestionFrameOptions = {
    definition?: CustomQuestionDefinition | null;
    mode: CustomQuestionModeName;
    settings?: Record<string, any>;
    value?: any;
    setValue?: (value: any) => void;
    answeredAt?: string | null;
    styles?: FormStyles | null;
    questionId?: number | string | null;
    customCss?: string | null;
    /** The submission being confirmed, on a confirmation section only */
    confirmation?: CustomQuestionConfirmation | null;
    /**
     * Cancel one of the confirmation's events, resolving with the
     *  confirmation as it stands afterwards. Throws an Error whose message is
     *  what the frame is told. Without one, breely.cancelEvent always rejects
     */
    /**
     * The logged in client's history, or null when nobody is. Without one,
     *  breely.getClientHistory always rejects
     */
    onGetClientHistory?: () => Promise<CustomQuestionClientHistory | null>;
    /**
     * Who is logged in, as anything that changes when they do. The frame is
     *  told whenever it changes, so a question can ask for the history again
     */
    clientKey?: string | null;
    onCancelEvent?: (
        eventId: any,
    ) => Promise<CustomQuestionConfirmation | null>;
};

/**
 * The host half of the custom question bridge.
 *
 * Owns the document the frame renders, the messages that cross between them,
 *  and how tall the frame ends up. The frame is on an opaque origin, so
 *  `event.origin` is the string 'null' and there is nothing there to check -
 *  what we check instead is that the message came from this frame's own
 *  window and carries the token minted for this mount.
 *
 * Because posting into an opaque origin means posting with targetOrigin '*',
 *  nothing but this question's own settings, its own answer, the form's theme
 *  and - on a confirmation section - the confirmation is ever sent down. The
 *  confirmation is built on the server to leave out anything that works as a
 *  credential, the submission's token included, which is why a cancel is a
 *  request the host carries out rather than something the frame can call.
 */
export default function useCustomQuestionFrame({
    definition,
    mode,
    settings,
    value,
    setValue,
    answeredAt,
    styles,
    questionId,
    customCss,
    confirmation,
    onCancelEvent,
    onGetClientHistory,
    clientKey,
}: UseCustomQuestionFrameOptions) {
    const frameRef = useRef<HTMLIFrameElement | null>(null);

    // One token per mount, so a stale frame left over from a previous render
    //  can't talk to this one. Deliberately re-minted when the question or the
    //  situation changes, which the token itself doesn't read
    const token = useMemo(
        () => `cq-${Math.random().toString(36).slice(2)}-${Date.now()}`,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [definition?.id, definition?.version, mode],
    );

    const [isReady, setIsReady] = useState(false);
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const [height, setHeight] = useState<number>(
        definition?.flags?.minHeight || DEFAULT_MIN_HEIGHT,
    );
    const [validity, setValidity] = useState<{
        isValid: boolean;
        message: string;
    }>({ isValid: true, message: '' });
    const [charges, setCharges] = useState<Array<Record<string, any>>>([]);
    const [frameError, setFrameError] = useState<string>('');

    const resolvedSettings = useMemo(
        () => resolveSettings(definition?.fields || [], settings || {}),
        [definition?.fields, settings],
    );

    // Compared by value: a form re-render hands down a new object every time,
    //  and rebuilding the document would restart the question
    const settingsKey = JSON.stringify(resolvedSettings);
    const valueKey = JSON.stringify(value ?? null);
    const confirmationKey = JSON.stringify(confirmation ?? null);

    const onCancelEventRef = useRef(onCancelEvent);
    onCancelEventRef.current = onCancelEvent;

    const onGetClientHistoryRef = useRef(onGetClientHistory);
    onGetClientHistoryRef.current = onGetClientHistory;

    // The answer the frame last told us about. Echoing it straight back would
    //  reset a half-typed value on every render
    const lastFromFrame = useRef<string | undefined>(undefined);
    const setValueRef = useRef(setValue);
    setValueRef.current = setValue;

    // The answer as it stands, for the message handler to merge charges into
    const valueRef = useRef<any>(value);
    valueRef.current = value;

    const srcDoc = useMemo(() => {
        if (!definition) {
            return '';
        }

        return buildSandboxDocument({
            definition,
            mode,
            token,
            settings: resolvedSettings,
            value,
            answeredAt,
            styles,
            questionId,
            customCss,
            confirmation,
        });
        // The value and confirmation are seeded into the document once and then kept in step by
        //  message, so it is deliberately not a dependency - rebuilding the
        //  document on every keystroke would restart the question
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        definition?.id,
        definition?.version,
        definition?.html,
        definition?.css,
        definition?.js,
        mode,
        token,
        settingsKey,
        styles?.color,
        styles?.font,
        styles?.fontSize,
        customCss,
        questionId,
    ]);

    const post = useCallback(
        (type: string, payload: any) => {
            const target = frameRef.current?.contentWindow;

            if (!target) {
                return;
            }

            // '*' is the only option for an opaque origin, which is why
            //  nothing sensitive goes through here
            target.postMessage({ __breely: token, type, payload }, '*');
        },
        [token],
    );

    useEffect(() => {
        function onMessage(event: MessageEvent) {
            const frameWindow = frameRef.current?.contentWindow;

            if (
                !frameWindow ||
                event.source !== frameWindow ||
                !event.data ||
                event.data.__breely !== token
            ) {
                return;
            }

            const { type, payload } = event.data;

            switch (type) {
                case 'ready':
                    setIsReady(true);
                    setFrameError('');

                    if (payload?.height) {
                        setHeight(clampHeight(payload.height, definition));
                    }
                    break;

                case 'value':
                    lastFromFrame.current = JSON.stringify(payload ?? null);
                    setValueRef.current?.(payload);
                    break;

                case 'valid':
                    setValidity({
                        isValid: payload?.isValid !== false,
                        message: payload?.message || '',
                    });
                    break;

                case 'charges': {
                    const next = Array.isArray(payload) ? payload : [];
                    setCharges(next);

                    // The server prices these from the question's own settings
                    //  when the form is submitted, reading them off the answer
                    //  - so they have to be part of it rather than sitting in
                    //  state up here
                    const merged = withCharges(valueRef.current, next);

                    if (merged !== valueRef.current) {
                        // Record it as the frame's own value so the effect
                        //  below doesn't post it straight back down again
                        lastFromFrame.current = JSON.stringify(merged ?? null);
                        setValueRef.current?.(merged);
                    }
                    break;
                }

                case 'height':
                    setHeight(clampHeight(payload, definition));
                    break;

                case 'error':
                    setFrameError(`${payload ?? ''}`);
                    break;

                case 'cancelEvent': {
                    const cancel = onCancelEventRef.current;

                    answer(
                        payload?.requestId,
                        cancel &&
                            (() =>
                                cancel(payload?.eventId).then(next => ({
                                    confirmation: next,
                                }))),
                        'The event could not be canceled',
                    );
                    break;
                }

                case 'getClientHistory': {
                    const getHistory = onGetClientHistoryRef.current;

                    answer(
                        payload?.requestId,
                        getHistory &&
                            (() =>
                                getHistory().then(history => ({
                                    data: history,
                                }))),
                        'The client history could not be loaded',
                    );
                    break;
                }
            }
        }

        /**
         * Carry out a request from the frame and send back a result naming
         *  the same requestId, which the bridge settles its promise with.
         *  Without a way to do it here, the request is not-available
         */
        function answer(
            requestId: unknown,
            run: (() => Promise<Record<string, unknown>>) | undefined,
            fallbackError: string,
        ) {
            if (typeof requestId !== 'number') {
                return;
            }

            if (!run) {
                post('result', {
                    requestId,
                    ok: false,
                    error: RequestError.NOT_AVAILABLE,
                });
                return;
            }

            run()
                .then(result =>
                    post('result', { requestId, ok: true, ...result }),
                )
                .catch(err =>
                    post('result', {
                        requestId,
                        ok: false,
                        error: err?.message || fallbackError,
                    }),
                );
        }

        window.addEventListener('message', onMessage);

        return () => window.removeEventListener('message', onMessage);
    }, [token, definition, post]);

    // A question that never reports in is either broken or waiting on
    //  something that isn't coming. Say so rather than showing an empty gap
    useEffect(() => {
        setIsReady(false);
        setHasTimedOut(false);

        if (!definition) {
            return;
        }

        const timer = setTimeout(() => setHasTimedOut(true), READY_TIMEOUT_MS);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [srcDoc, definition?.id]);

    // Keep the frame's copy of the answer in step with ours, ex. when a past
    //  answer is prefilled after the question has already started
    useEffect(() => {
        if (!isReady) {
            return;
        }

        const encoded = valueKey;

        if (encoded === lastFromFrame.current) {
            return;
        }

        lastFromFrame.current = encoded;
        post('value', value ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, valueKey, post]);

    // The client logging in or out part way through doesn't change anything
    //  the frame was given, so it is told separately. Not on the first render
    //  - the question asks for the history itself once it starts
    const lastClientKey = useRef(clientKey ?? null);

    useEffect(() => {
        if (!isReady || lastClientKey.current === (clientKey ?? null)) {
            return;
        }

        lastClientKey.current = clientKey ?? null;
        post('clientChanged', null);
    }, [isReady, clientKey, post]);

    // Same for the confirmation, ex. once the page has refetched it after an
    //  event was canceled somewhere else on it
    useEffect(() => {
        if (isReady) {
            post('confirmation', confirmation ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, confirmationKey, post]);

    useEffect(() => {
        if (isReady) {
            post('settings', resolvedSettings);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, settingsKey, post]);

    return {
        frameRef,
        srcDoc,
        height,
        isReady,
        hasTimedOut: hasTimedOut && !isReady,
        validity,
        charges,
        frameError,
        resolvedSettings,
        isInteractive: mode === CustomQuestionMode.FILL,
    };
}

/**
 * Puts what a question asked to charge for onto its answer.
 *
 * Charging needs the keys and quantities to reach the server, and the only
 *  thing a question submits is its answer - so they ride along under
 *  `charges`, which is where CustomQuestionCharges looks for them.
 *
 * An answer that isn't an object has nowhere to put them. That is left alone
 *  rather than reshaped, since reshaping it would break the question's own
 *  summary and context fields; docs/custom-questions.md says a question that
 *  charges has to set an object answer.
 */
function withCharges(value: any, charges: Array<Record<string, any>>): any {
    const isPlainObject =
        !!value && typeof value === 'object' && !Array.isArray(value);

    if (!charges.length) {
        if (!isPlainObject || value.charges === undefined) {
            return value;
        }

        const rest = { ...value };
        delete rest.charges;

        return rest;
    }

    if (value === null || value === undefined) {
        return { charges };
    }

    if (!isPlainObject) {
        return value;
    }

    return { ...value, charges };
}

function clampHeight(
    height: any,
    definition?: CustomQuestionDefinition | null,
): number {
    const px = parseInt(`${height}`, 10);

    if (isNaN(px)) {
        return definition?.flags?.minHeight || DEFAULT_MIN_HEIGHT;
    }

    const min = definition?.flags?.minHeight || DEFAULT_MIN_HEIGHT;
    const max = Math.min(
        definition?.flags?.maxHeight || MAX_FRAME_HEIGHT,
        MAX_FRAME_HEIGHT,
    );

    return Math.max(min, Math.min(px, max));
}
